import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import type { Key } from 'react'
import { useRef } from 'react'

import DialerController from '@src/app/command/DialerCommand/store/DialerController'
import ForwardPhoneNumberSelectorController from '@src/app/command/forward-phone-number-picker/ForwardPhoneNumberSelectorController'
import AddParticipantController from '@src/app/voice/store/AddParticipantController'
import CallTransferController from '@src/app/voice/store/CallTransferController'
import { HeaderItem } from '@src/component/menu-v2'
import { highlight } from '@src/lib'
import isNonNull from '@src/lib/isNonNull'
import { formatted } from '@src/lib/phone-number'
import type { IdentityPhone } from '@src/service/model'
import type { ThemeKey } from '@src/theme'
import Menu from '@ui/Menu'
import Tooltip from '@ui/Tooltip'
import Typography from '@ui/Typography'
import { BusinessHoursIcon } from '@ui/icons/tint/16/general'

import { ContactRow, MemberRow } from './PhoneNumberSelectorController'
import type { IdentityRow, Row } from './PhoneNumberSelectorController'
import type PhoneNumberSelectorController from './PhoneNumberSelectorController'
import RowItem from './row-item'
import * as styles from './row.css'

interface PhoneNumberSelectorRowProps {
  controller: PhoneNumberSelectorController
  overlayThemeKey?: ThemeKey
  row: Row
}

const PhoneNumberSelectorRow = ({
  controller,
  overlayThemeKey,
  row,
}: PhoneNumberSelectorRowProps) => {
  const rowRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const renderListItem = (phone: IdentityPhone) => {
    if (!(row instanceof MemberRow || row instanceof ContactRow)) {
      throw new Error('renderListItem cannot be used with this row type')
    }

    const isOffHours = phone.isOffHours
    const { disabled, tooltipDisabledReason, labelDisabledReason } =
      row.phoneNumberMeta.get(phone.number) ?? {}

    const phoneNameLabel = `${phone.symbol ? `${phone.symbol} ` : ''}${
      phone.name ?? 'Phone'
    }`

    return (
      <Menu.ListItem key={phone.number} disabled={disabled} textValue={phoneNameLabel}>
        <Tooltip
          themeKey={overlayThemeKey}
          key={phone.number}
          title={tooltipDisabledReason ?? ''}
          placement="top"
          disabled={!disabled || !tooltipDisabledReason}
        >
          <div className={styles.menuItem}>
            <Typography
              nowrap
              variant="footnote"
              color={disabled || isOffHours ? 'textTertiary' : 'textPrimary'}
              className={styles.menuItemPhoneName}
            >
              {phoneNameLabel}
              &nbsp;
            </Typography>
            <Typography
              nowrap
              variant="footnote"
              color={disabled || isOffHours ? 'textTertiary' : 'textSecondary'}
              className={cx(styles.highlight, styles.menuItemPhoneNumber)}
              dangerouslySetInnerHTML={{
                __html:
                  disabled && labelDisabledReason
                    ? labelDisabledReason
                    : highlight(formatted(phone.number) ?? '', controller.search),
              }}
            />
            {isOffHours && <BusinessHoursIcon className={styles.businessHours} />}
          </div>
        </Tooltip>
      </Menu.ListItem>
    )
  }

  const onMenuListItemClick = (number: Key, row: MemberRow | ContactRow) => {
    const phone = row.phones.find((phone) => phone.number === number)
    if (!phone) {
      return
    }

    controller.handleSelect(
      row.type === 'contact'
        ? {
            type: 'contact',
            to: row.identity,
            via: phone.number,
          }
        : {
            type: 'member',
            to: row.identity,
            via: phone.number,
          },
    )
  }

  switch (row.type) {
    case 'header':
      return (
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        <HeaderItem className={styles.header}>
          <Typography variant="caption1" color="textTertiary">
            {row.title}
          </Typography>
        </HeaderItem>
      )
    case 'contact':
    case 'member': {
      const listItems =
        row.type === 'contact'
          ? row.phones.map((phone) => renderListItem(phone))
          : [
              row.privatePhones.length > 0 ? (
                <Menu.Section title="Private numbers" key="private-numbers">
                  {row.privatePhones.map((phone) => renderListItem(phone))}
                </Menu.Section>
              ) : null,
              row.sharedPhones.length > 0 ? (
                <Menu.Section title="Shared numbers" key="shared-numbers">
                  {row.sharedPhones.map((phone) => renderListItem(phone))}
                </Menu.Section>
              ) : null,
            ].filter(isNonNull)

      return (
        <>
          {row.phones.length > 1 &&
          (row.type === 'contact' ||
            (row.type === 'member' &&
              // FIXME: this is hack due to the current nerf of direct dialing
              (controller instanceof CallTransferController ||
                controller instanceof DialerController ||
                controller instanceof ForwardPhoneNumberSelectorController ||
                controller instanceof AddParticipantController))) ? (
            <>
              <Menu.Provider
                isOpen={controller.activeRow === row}
                targetRef={rowRef}
                onClose={() => controller.closeMenu()}
              >
                <Menu.List
                  themeKey={overlayThemeKey}
                  onAction={(number) => onMenuListItemClick(number, row)}
                  placement="top right"
                >
                  {listItems}
                </Menu.List>
              </Menu.Provider>
            </>
          ) : null}
          <RowItem ref={rowRef} controller={controller} row={row} />
        </>
      )
    }
    case 'inbox-member':
    case 'inbox':
    case 'number':
    case 'add-number':
      return <RowItem controller={controller} row={row as IdentityRow} />
  }
}

export default observer(PhoneNumberSelectorRow)
